import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Copy, Mail } from "lucide-react";

interface Deal {
  id: string;
  deal_title: string;
  property_address?: string;
  seller_name: string;
  seller_email?: string;
  seller_phone?: string;
  seller_address?: string;
  buyer_name: string;
  buyer_address?: string;
  sales_price?: number;
  down_payment_amount?: number;
  interest_rate?: number;
  payment_amount?: number;
  original_term_months?: number;
  date_of_sale?: string;
  current_balance?: number;
  payments_remaining?: number;
  has_balloon_payment?: boolean;
  balloon_amount?: number;
  balloon_due_date?: string;
  estimated_property_value?: number;
  buyer_payment_history?: string;
  buyer_current_on_contract?: boolean;
  property_description?: string;
  zillow_link?: string;
  internal_notes?: string;
}
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function UnderwriterQuoteForm() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [formattedData, setFormattedData] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    const { data, error } = await supabase
      .from('deals')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching deals:', error);
      toast({
        title: "Error",
        description: "Failed to load deals",
        variant: "destructive",
      });
      return;
    }

    setDeals(data || []);
  };

  const formatCurrency = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatDate = (date?: string) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US');
  };

  const formatPercentage = (value?: number) => {
    if (!value) return 'N/A';
    return `${value}%`;
  };

  const generateFormattedData = (deal: Deal) => {
    return `UNDERWRITER QUOTE REQUEST

Deal: ${deal.deal_title}
Property Address: ${deal.property_address || 'N/A'}

CONTACT INFORMATION
Seller Name: ${deal.seller_name}
Email: ${deal.seller_email || 'N/A'}
Phone: ${deal.seller_phone || 'N/A'}
${deal.seller_address ? `Address: ${deal.seller_address}` : ''}

BUYER INFORMATION
Buyer Name: ${deal.buyer_name}
Buyer Address: ${deal.buyer_address || 'N/A'}
Current on Contract: ${deal.buyer_current_on_contract ? 'Yes' : 'No'}

ORIGINAL CONTRACT TERMS
Sales Price: ${formatCurrency(deal.sales_price)}
Down Payment: ${formatCurrency(deal.down_payment_amount)}
Interest Rate: ${formatPercentage(deal.interest_rate)}
Monthly Payment: ${formatCurrency(deal.payment_amount)}
Original Term: ${deal.original_term_months ? `${deal.original_term_months} months` : 'N/A'}
Sales Date: ${formatDate(deal.date_of_sale)}

CURRENT STATUS
Current Balance: ${formatCurrency(deal.current_balance)}
Payments Remaining: ${deal.payments_remaining || 'N/A'}
${deal.has_balloon_payment ? `\nBALLOON PAYMENT
Amount: ${formatCurrency(deal.balloon_amount)}
Due Date: ${formatDate(deal.balloon_due_date)}` : ''}

PROPERTY DETAILS
Estimated Property Value: ${formatCurrency(deal.estimated_property_value)}
Property Description: ${deal.property_description || 'N/A'}

PAYMENT HISTORY
${deal.buyer_payment_history || 'N/A'}

PROPERTY RESEARCH LINKS
${deal.zillow_link ? `Zillow: ${deal.zillow_link}` : ''}

${deal.internal_notes ? `NOTES\n${deal.internal_notes}` : ''}`;
  };

  const handleDealSelect = (dealId: string) => {
    const deal = deals.find(d => d.id === dealId);
    if (deal) {
      setSelectedDeal(deal);
      const formatted = generateFormattedData(deal);
      setFormattedData(formatted);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(formattedData);
    toast({
      title: "Copied!",
      description: "Quote request copied to clipboard",
    });
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`Quote Request: ${selectedDeal?.deal_title || 'Deal'}`);
    const body = encodeURIComponent(formattedData);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Underwriter Quote Request</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="deal-select">Select Deal</Label>
          <Select onValueChange={handleDealSelect}>
            <SelectTrigger id="deal-select">
              <SelectValue placeholder="Choose a deal..." />
            </SelectTrigger>
            <SelectContent>
              {deals.map((deal) => (
                <SelectItem key={deal.id} value={deal.id}>
                  {deal.deal_title} - {deal.property_address || 'No address'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedDeal && (
          <>
            <div>
              <Label htmlFor="formatted-data">Formatted Quote Request</Label>
              <Textarea
                id="formatted-data"
                value={formattedData}
                readOnly
                className="font-mono text-sm h-96"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleCopy} className="flex-1">
                <Copy className="w-4 h-4 mr-2" />
                Copy to Clipboard
              </Button>
              <Button onClick={handleEmail} variant="outline" className="flex-1">
                <Mail className="w-4 h-4 mr-2" />
                Email Request
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
